/* Woolly Hollow — the stop-motion engine.
   One 12 fps clock drives the poses that should look hand-animated: boiling (each placed thing nudged a hair per frame),
   fuzzy headline seeds, walking bobs. Anything that travels or follows the pointer runs every display frame (WH.onTick),
   and tweens are smooth: stepping those at 12 fps read as a slow computer, not as stop-motion.
   Also: lazy section registry, synthesized sounds, felting-needle cursor, clapperboard, header, tote (cart), Fuzzy Club pop-up. */
(function () {
  'use strict';
  var root = document.documentElement;
  var WH = window.WH = window.WH || {};
  var mm = function (q) { return window.matchMedia && matchMedia(q).matches; };
  WH.reduce = mm('(prefers-reduced-motion: reduce)');
  WH.fine = mm('(hover: hover) and (pointer: fine)');
  WH.FPS = 12;
  WH.clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };
  WH.rand = function (a, b) { return a + Math.random() * (b - a); };
  WH.anim = !WH.reduce && !!window.gsap;
  /* prices in the shop's own format (e.g. "CHF {{amount}}") */
  WH.money = function (cents) {
    var f = window.WH_MONEY || '{{amount}}', n = (cents / 100);
    var two = n.toFixed(2), comma = two.replace('.', ','), noDec = Math.round(n).toString();
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      return k === 'amount_with_comma_separator' ? comma : k === 'amount_no_decimals' ? noDec : k === 'amount_no_decimals_with_comma_separator' ? noDec : two;
    });
  };
  if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
  if (window.gsap && window.Flip) gsap.registerPlugin(Flip);

  /* ---------- tiny event bus ---------- */
  var bus = {};
  WH.on = function (n, fn) { (bus[n] = bus[n] || []).push(fn); };
  WH.emit = function (n, d) { (bus[n] || []).slice().forEach(function (fn) { fn(d); }); };

  /* ---------- the 12 fps clock (poses) + every display frame (motion) ---------- */
  var subs = [], ticks = [], frame = 0, acc = 0, last = 0, running = false;
  WH.onFrame = function (fn) { subs.push(fn); if (!running) start(); return function () { subs = subs.filter(function (f) { return f !== fn; }); }; };
  /* fn(dt in seconds, now): for travelling and pointer-following motion */
  WH.onTick = function (fn) { ticks.push(fn); if (!running) start(); return function () { ticks = ticks.filter(function (f) { return f !== fn; }); }; };
  /* 0..1 share to close toward a target this tick, matching "k per 12 fps frame" at any refresh rate */
  WH.ease = function (k, dt) { return 1 - Math.pow(1 - k, dt * WH.FPS); };
  function start() { running = true; last = performance.now(); requestAnimationFrame(tick); }
  function tick(now) {
    if (document.hidden) { last = now; requestAnimationFrame(tick); return; }
    var dt = Math.min(0.05, (now - last) / 1000);
    acc += now - last; last = now;
    for (var j = 0; j < ticks.length; j++) { try { ticks[j](dt, now); } catch (e) { if (window.console) console.error(e); } }
    if (acc >= 1000 / WH.FPS) {
      acc = acc % (1000 / WH.FPS);
      frame++;
      for (var i = 0; i < subs.length; i++) { try { subs[i](frame); } catch (e) { if (window.console) console.error(e); } }
    }
    requestAnimationFrame(tick);
  }
  WH.frame = function () { return frame; };

  /* the ease for character tweens. It used to advance only on whole 12 fps frames; now it is the plain smooth ease
     (callers keep the same signature, the second argument is ignored) */
  WH.sm = function (ease) {
    return window.gsap ? gsap.parseEase(ease || 'none') : function (p) { return p; };
  };

  /* ---------- boil ---------- */
  var boilers = new Set();
  if (!WH.reduce && WH.fine && 'IntersectionObserver' in window) {
    root.classList.add('wh-boils');
    var bio = new IntersectionObserver(function (en) { en.forEach(function (e) { if (e.isIntersecting) boilers.add(e.target); else boilers.delete(e.target); }); });
    WH.boil = function (scope) { (scope || document).querySelectorAll('[data-boil]').forEach(function (el) { bio.observe(el); }); };
    WH.onFrame(function (f) {
      if (f % 2) return; /* on twos */
      boilers.forEach(function (el) {
        var a = parseFloat(el.getAttribute('data-boil')) || 1;
        el.style.setProperty('--bx', (WH.rand(-0.7, 0.7) * a).toFixed(2) + 'px');
        el.style.setProperty('--by', (WH.rand(-0.7, 0.7) * a).toFixed(2) + 'px');
        el.style.setProperty('--br', (WH.rand(-0.45, 0.45) * a).toFixed(2) + 'deg');
      });
    });
    /* fuzzy headline edges: swap the noise seed on twos so the letters crawl like hand-cut felt (fine pointers only, it repaints) */
    var seedEl = document.querySelector('[data-boil-seed]');
    if (seedEl && WH.fine) {
      var fuzzOn = 0;
      var fio = new IntersectionObserver(function (en) { en.forEach(function (e) { fuzzOn += e.isIntersecting ? 1 : -1; }); fuzzOn = Math.max(0, fuzzOn); });
      document.querySelectorAll('.wh-fuzz').forEach(function (el) { fio.observe(el); });
      /* the crawl waits while the page is scrolling: the headings are moving anyway, and each crawl step repaints them */
      WH.onFrame(function (f) { if (fuzzOn && f % 2 === 0 && performance.now() - WH.scroll.t > 220) seedEl.setAttribute('seed', String(1 + (f / 2) % 3)); });
    }
  } else {
    WH.boil = function () {};
  }

  /* ---------- scroll direction + speed ---------- */
  WH.scroll = { dir: 1, v: 0, y: window.scrollY, t: 0 };
  window.addEventListener('scroll', function () {
    var y = window.scrollY, d = y - WH.scroll.y;
    WH.scroll.t = performance.now();
    if (Math.abs(d) > 1) WH.scroll.dir = d > 0 ? 1 : -1;
    WH.scroll.v = WH.scroll.v * 0.6 + d * 0.4;
    WH.scroll.y = y;
  }, { passive: true });
  WH.onFrame(function () { WH.scroll.v *= 0.7; });

  /* ---------- lazy sections ---------- */
  var defs = {};
  WH.register = function (name, fn) {
    defs[name] = fn;
    document.querySelectorAll('[data-wh="' + name + '"]').forEach(arm);
  };
  function arm(el) {
    if (el.__whArmed) return;
    el.__whArmed = true;
    var go = function () { if (el.__whInit) return; el.__whInit = true; try { defs[el.getAttribute('data-wh')](el); } catch (e) { if (window.console) console.error(e); } };
    if (el.hasAttribute('data-wh-eager') || !('IntersectionObserver' in window)) return go();
    var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); go(); } }, { rootMargin: '500px 0px' });
    io.observe(el);
  }

  /* ---------- stop-motion pop-ins ---------- */
  function splitWords(el) {
    if (el.__split) return el.__split;
    var out = [];
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var parts = n.textContent.split(/(\s+)/), frag = document.createDocumentFragment();
          parts.forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
            var s = document.createElement('span'); s.className = 'wh-word'; s.textContent = p; frag.appendChild(s); out.push(s);
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && !n.classList.contains('wh-word')) walk(n);
      });
    })(el);
    return (el.__split = out);
  }
  WH.splitWords = splitWords;
  function pop(el) {
    el.classList.add('is-in');
    if (!WH.anim) return;
    var words = el.hasAttribute('data-pop-words') ? splitWords(el) : null;
    var targets = words && words.length ? words : [el];
    gsap.fromTo(targets, { scale: 0.2, rotation: function () { return WH.rand(-14, 14); }, y: 24, opacity: 0 },
      { scale: 1, rotation: 0, y: 0, opacity: 1, duration: 0.5, ease: WH.sm('back.out(2.6)', 0.5), stagger: words ? 0.07 : 0, transformOrigin: '50% 80%',
        delay: parseFloat(el.getAttribute('data-pop-delay')) || 0, clearProps: words ? 'transform' : 'transform,opacity' });
  }
  WH.pops = function (scope) {
    var els = (scope || document).querySelectorAll('[data-pop]:not(.is-in)');
    if (!('IntersectionObserver' in window)) { els.forEach(pop); return; }
    var io = new IntersectionObserver(function (en) { en.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); pop(e.target); } }); }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  };

  /* ---------- sound (synthesized, nothing to download; off until the visitor turns it on) ---------- */
  var actx = null, soundOn = false;
  try { soundOn = localStorage.getItem('wh-sound') === '1' || (localStorage.getItem('wh-sound') === null && document.body.getAttribute('data-sound') === 'true'); } catch (e) {}
  function ac() { if (!actx) { var C = window.AudioContext || window.webkitAudioContext; if (C) actx = new C(); } return actx; }
  function tone(type, f0, f1, dur, vol, delay) {
    var c = ac(); if (!c) return;
    var t = c.currentTime + (delay || 0), o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f0, t); if (f1) o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.012); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(c.destination); o.start(t); o.stop(t + dur + 0.02);
  }
  function noise(dur, vol, freq) {
    var c = ac(); if (!c) return;
    var n = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate), d = n.getChannelData(0);
    for (var i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    var s = c.createBufferSource(), f = c.createBiquadFilter(), g = c.createGain();
    s.buffer = n; f.type = 'bandpass'; f.frequency.value = freq || 1800; g.gain.value = vol;
    s.connect(f).connect(g).connect(c.destination); s.start();
  }
  var SOUNDS = {
    pop: function () { tone('sine', 700, 180, 0.09, 0.22); },
    squeak: function () { tone('triangle', 900, 1500, 0.07, 0.12); tone('triangle', 1500, 1100, 0.08, 0.1, 0.07); },
    clack: function () { noise(0.05, 0.5, 2600); tone('square', 180, 90, 0.05, 0.06); },
    ding: function () { tone('sine', 1320, null, 0.9, 0.18); tone('sine', 2640, null, 0.5, 0.05); },
    drip: function () { tone('sine', 1400, 520, 0.08, 0.14); },
    thud: function () { tone('sine', 160, 70, 0.14, 0.26); },
    rustle: function () { noise(0.22, 0.35, 900); },
    boing: function () { tone('sine', 220, 660, 0.18, 0.16); tone('sine', 660, 330, 0.16, 0.1, 0.16); }
  };
  WH.sound = {
    get on() { return soundOn; },
    play: function (n) { if (!soundOn || !SOUNDS[n]) return; try { SOUNDS[n](); } catch (e) {} },
    toggle: function () {
      soundOn = !soundOn;
      try { localStorage.setItem('wh-sound', soundOn ? '1' : '0'); } catch (e) {}
      if (soundOn) { var c = ac(); if (c && c.state === 'suspended') c.resume(); SOUNDS.boing(); }
      syncSound();
    }
  };
  function syncSound() {
    document.querySelectorAll('[data-sound-toggle]').forEach(function (b) { b.setAttribute('aria-pressed', soundOn ? 'true' : 'false'); });
    root.classList.toggle('wh-is-sound', soundOn);
  }

  /* ---------- a little puff of wool where you click (fine pointers; the felt cursors themselves are plain CSS) ---------- */
  function fluffClicks() {
    if (!WH.fine || WH.reduce) return;
    window.addEventListener('pointerdown', function (e) { if (e.pointerType === 'mouse') puff(e.clientX, e.clientY, 4); }, { passive: true });
  }
  var COLORS = ['#F2B33D', '#F4A3BC', '#86B85A', '#9FD0EE', '#E2483A', '#FFF8EC'];
  function puff(px, py, n, big) {
    if (!WH.anim) return;
    for (var i = 0; i < (n || 5); i++) {
      var d = document.createElement('i');
      d.className = 'wh-fluff';
      d.style.left = px + 'px'; d.style.top = py + 'px';
      d.style.background = COLORS[(Math.random() * COLORS.length) | 0];
      document.body.appendChild(d);
      var a = Math.random() * Math.PI * 2, r = WH.rand(16, big ? 90 : 38);
      gsap.fromTo(d, { x: 0, y: 0, scale: WH.rand(0.6, 1.3) }, { x: Math.cos(a) * r, y: Math.sin(a) * r - 10, scale: 0, duration: WH.rand(0.4, 0.8), ease: WH.sm('power2.out', 0.6), onComplete: d.remove.bind(d) });
    }
  }
  WH.puff = puff;

  /* ---------- clapperboard ---------- */
  function clapper() {
    var clap = document.querySelector('[data-clap]');
    var scenes = Array.prototype.slice.call(document.querySelectorAll('[data-scene]'));
    if (!clap || scenes.length < 2) return;
    clap.hidden = false;
    var no = clap.querySelector('[data-clap-no]'), title = clap.querySelector('[data-clap-title]'), stick = clap.querySelector('[data-clap-stick]');
    var list = clap.querySelector('[data-clap-list]'), toggle = clap.querySelector('[data-clap-toggle]');
    scenes.forEach(function (s, i) {
      var li = document.createElement('li'), a = document.createElement('a');
      a.href = '#' + s.id; a.textContent = s.getAttribute('data-scene');
      a.setAttribute('data-n', String(i).padStart(2, '0'));
      li.appendChild(a); list.appendChild(li);
    });
    toggle.addEventListener('click', function () { var o = clap.classList.toggle('is-open'); toggle.setAttribute('aria-expanded', o ? 'true' : 'false'); });
    list.addEventListener('click', function () { clap.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); });
    var cur = -1, restT = 0;
    function set(i, quiet) {
      if (i === cur) return;
      cur = i;
      var go = function () { no.textContent = String(i).padStart(2, '0'); title.textContent = scenes[i].getAttribute('data-scene'); };
      list.querySelectorAll('a').forEach(function (a, k) { a.classList.toggle('is-here', k === i); });
      if (quiet || !WH.anim) { go(); return; }
      gsap.timeline()
        .to(stick, { rotation: -24, duration: 0.25, ease: WH.sm('power2.out', 0.25) })
        .add(go)
        .to(stick, { rotation: 0, duration: 0.17, ease: WH.sm('power3.in', 0.17), onComplete: function () { WH.sound.play('clack'); clap.classList.add('is-clack'); setTimeout(function () { clap.classList.remove('is-clack'); }, 160); } });
    }
    var tops = [];
    function measure() { var y = window.scrollY; tops = scenes.map(function (s) { return s.getBoundingClientRect().top + y; }); }
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(document.body);
    function which() {
      var mid = window.scrollY + window.innerHeight * 0.45, best = 0;
      for (var i = 0; i < tops.length; i++) if (tops[i] <= mid) best = i;
      return best;
    }
    set(which(), true);
    window.addEventListener('scroll', function () {
      set(which());
      clap.classList.add('is-moving'); clearTimeout(restT);
      restT = setTimeout(function () { clap.classList.remove('is-moving'); }, 1600);
    }, { passive: true });
    clap.classList.toggle('is-over-end', false);
  }

  /* ---------- header ---------- */
  function header() {
    var h = document.querySelector('[data-header]');
    if (!h) return;
    var lastY = window.scrollY;
    /* 10-30 owner: on phones the header stays at the top while scrolling down too (only wider screens still tuck it away) */
    var keep = window.matchMedia ? window.matchMedia('(max-width: 900px)') : { matches: false };
    function onScroll() {
      var y = window.scrollY;
      h.classList.toggle('is-scrolled', y > 24);
      if (keep.matches) h.classList.remove('is-hidden');
      else if (!root.classList.contains('wh-is-menu')) h.classList.toggle('is-hidden', y > 320 && y > lastY + 2);
      if (y < lastY - 2) h.classList.remove('is-hidden');
      lastY = y;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    var menu = document.querySelector('[data-menu]'), btn = document.querySelector('[data-menu-toggle]');
    if (menu && btn) {
      var setMenu = function (open) {
        root.classList.toggle('wh-is-menu', open);
        menu.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && WH.anim) gsap.fromTo(menu.querySelectorAll('[data-menu-item]'), { y: 40, rotation: function () { return WH.rand(-8, 8); }, opacity: 0 }, { y: 0, rotation: 0, opacity: 1, duration: 0.45, stagger: 0.05, ease: WH.sm('back.out(2)', 0.45) });
      };
      btn.addEventListener('click', function () { setMenu(menu.hidden); });
      menu.addEventListener('click', function (e) { if (e.target.closest('a')) setMenu(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !menu.hidden) setMenu(false); });
    }
    /* signposts: hovering or focusing an item opens its board (CSS); the chevron opens it by tap or keyboard;
       Escape or a click elsewhere puts it away */
    var items = Array.prototype.slice.call(document.querySelectorAll('[data-nav-item]'));
    items.forEach(function (item) {
      var more = item.querySelector('[data-nav-more]');
      var set = function (open) { item.classList.toggle('is-open', open); if (more) more.setAttribute('aria-expanded', open ? 'true' : 'false'); };
      if (more) more.addEventListener('click', function (e) { e.preventDefault(); item.classList.remove('is-shut'); set(!item.classList.contains('is-open')); });
      item.addEventListener('keydown', function (e) { if (e.key === 'Escape') { set(false); item.classList.add('is-shut'); if (more) more.focus(); } });
      item.addEventListener('mouseleave', function () { item.classList.remove('is-shut'); });
      item.addEventListener('focusout', function (e) { if (!item.contains(e.relatedTarget)) { set(false); item.classList.remove('is-shut'); } });
    });
    if (items.length) document.addEventListener('click', function (e) {
      items.forEach(function (item) {
        if (item.classList.contains('is-open') && !item.contains(e.target)) { item.classList.remove('is-open'); var b = item.querySelector('[data-nav-more]'); if (b) b.setAttribute('aria-expanded', 'false'); }
      });
    });
    document.querySelectorAll('[data-sound-toggle]').forEach(function (b) { b.addEventListener('click', WH.sound.toggle); });
    syncSound();
  }

  /* ---------- tote (cart) ---------- */
  var cart = WH.cart = {};
  function drawer() { return document.querySelector('[data-tote]'); }
  cart.open = function () {
    var d = drawer(); if (!d) return;
    d.hidden = false; root.classList.add('wh-is-tote');
    requestAnimationFrame(function () { d.classList.add('is-open'); });
    var f = d.querySelector('[data-tote-close]'); if (f) f.focus({ preventScroll: true });
  };
  cart.close = function () {
    var d = drawer(); if (!d) return;
    d.classList.remove('is-open'); root.classList.remove('wh-is-tote');
    setTimeout(function () { if (!d.classList.contains('is-open')) d.hidden = true; }, 420);
  };
  function renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      var tmp = document.createElement('div'); tmp.innerHTML = sections['cart-drawer'];
      var fresh = tmp.querySelector('[data-tote-body]'), cur = document.querySelector('[data-tote-body]');
      if (fresh && cur) cur.replaceWith(fresh);
    }
  }
  function setCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = n; el.hidden = !n; });
  }
  cart.count = setCount;
  /* formData: a product form's FormData, or a plain { items: [{ id, quantity }, ...] } for several friends at once (gang of four) */
  /* 10-26 owner: the + on a card used to open the tote, so going on shopping meant closing it again, and the card itself didn't
     say it was added. Cards (form.wh-box__add) now stay on the page: the friend hops into the bag, the bag count goes up and the
     card shows a "2 in your tote" badge (also on arrival, and after the tote changes). The product page still opens the tote. */
  var TICK = '<svg class="wh-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5.5 12.5 4 4 9-9.5"/></svg>';
  function markBoxes(items, pop) {
    var forms = document.querySelectorAll('form.wh-box__add');
    if (!forms.length || !items) return;
    var q = {};
    items.forEach(function (i) { q[i.variant_id] = (q[i.variant_id] || 0) + i.quantity; });
    forms.forEach(function (f) {
      var id = (f.querySelector('[name="id"]') || {}).value, n = q[id] || 0, box = f.closest('.wh-box') || f.parentNode;
      var b = box.querySelector('[data-box-in]');
      if (!n) { if (b) b.remove(); return; }
      if (!b) { b = document.createElement('span'); b.className = 'wh-box__in'; b.setAttribute('data-box-in', ''); b.setAttribute('role', 'status'); box.appendChild(b); }
      var was = b.getAttribute('data-n');
      b.setAttribute('data-n', n);
      b.innerHTML = TICK + '<span>' + (f.getAttribute('data-in') || '%n').replace('%n', n) + '</span>';
      if (pop && was !== String(n) && pop === f) { b.classList.remove('is-pop'); void b.offsetWidth; b.classList.add('is-pop'); }
    });
  }
  function cartJson() { return fetch((window.WH_ROUTES && WH_ROUTES.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }
  cart.mark = markBoxes;
  cart.add = function (formData, srcImg, opts) {
    var req;
    if (window.FormData && formData instanceof FormData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      req = { method: 'POST', body: formData, headers: { Accept: 'application/json' } };
    } else {
      formData.sections = 'cart-drawer';
      formData.sections_url = window.location.pathname;
      req = { method: 'POST', body: JSON.stringify(formData), headers: { Accept: 'application/json', 'Content-Type': 'application/json' } };
    }
    return fetch((window.WH_ROUTES && WH_ROUTES.cartAdd || '/cart/add') + '.js', req)
      /* Shopify's own refusals come as JSON in the shopper's language; anything else (a bot check page, a dropped line) gets
         the tote's plain "try again" instead of a parser message */
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          var d = drawer();
          if (!r.ok || !(j.id || j.items)) throw new Error(j.description || j.message || (d && d.getAttribute('data-add-failed')) || 'Could not add');
          return j;
        });
      })
      .then(function (j) {
        renderSections(j.sections);
        /* the fresh tote carries the count, so no second trip to /cart.js before it opens (about a second on a slow line) */
        var n = j.sections && document.querySelector('[data-tote] .wh-tote__n');
        if (n) return { item_count: parseInt(n.textContent, 10) || 0 };
        return fetch((window.WH_ROUTES && WH_ROUTES.cart || '/cart') + '.js').then(function (r) { return r.json(); });
      })
      .then(function (c) {
        setCount(c.item_count);
        var stay = opts && opts.stay;
        var go = function () { bagGulp(); if (!stay) cart.open(); };
        if (srcImg) fly(srcImg, go); else go();
        if (stay) cartJson().then(function (full) { markBoxes(full.items, opts.form); }).catch(function () {});
        cart.deal();
        return c;
      });
  };
  cart.change = function (line, qty) {
    var fd = new FormData(); fd.append('line', line); fd.append('quantity', qty); fd.append('sections', 'cart-drawer');
    return fetch((window.WH_ROUTES && WH_ROUTES.cartChange || '/cart/change') + '.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { renderSections(c.sections); setCount(c.item_count); markBoxes(c.items); return cart.deal(); });
  };
  /* buy 3, get 1 free, where the free friend may cost up to the dearest of the 3 paid (owner 10-25). Shopify's own
     buy-x-get-y frees the cheapest, so whenever the tote changes the deals worker (wh-deals) looks at this cart and hands
     back one-time codes that free the right ones; they go on the cart next to any code the customer typed. If it can't be
     reached, the store's automatic buy-3-get-1 still frees the cheapest. Resolves true when the cart's codes changed. */
  var OURS = /^BUY3GET1-/i, dealBusy = null, dealAgain = false, SETTLED = 'wh-deal-settled';
  function within(p, ms) { return Promise.race([p, new Promise(function (ok) { setTimeout(function () { ok(null); }, ms); })]); }
  /* a cart already settled (same friends, same codes, within 2 hours: our codes live at least 4) isn't asked about again,
     so checkout doesn't wait on the worker a second time */
  function settledSig(c, codes) {
    return c.token + '|' + c.items.map(function (i) { return i.variant_id + 'x' + i.quantity; }).sort().join() + '|' + codes.slice().sort().join();
  }
  function dealOnce(url) {
    var base = window.WH_ROUTES && WH_ROUTES.cart || '/cart';
    return fetch(base + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      var codes = (c.discount_codes || []).map(function (d) { return d.code; });
      var mine = codes.filter(function (x) { return OURS.test(x); }), theirs = codes.filter(function (x) { return !OURS.test(x); });
      if (c.item_count < 4 && !mine.length) return false;
      var good = (c.discount_codes || []).every(function (d) { return !OURS.test(d.code) || d.applicable; });
      try { var was = JSON.parse(sessionStorage.getItem(SETTLED) || 'null'); if (good && was && was.s === settledSig(c, mine) && Date.now() - was.t < 2 * 3600e3) return false; } catch (x) {}
      var settle = function (now) { try { sessionStorage.setItem(SETTLED, JSON.stringify({ s: settledSig(c, now), t: Date.now() })); } catch (x) {} };
      var ask = c.item_count < 4 ? Promise.resolve({ codes: [] }) : within(fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cart: c.token }) })
        .then(function (r) { return r.ok ? r.json() : null; }), 6000);
      return ask.then(function (d) {
        if (!d || !d.codes) return false;
        if (d.codes.slice().sort().join() === mine.slice().sort().join()) { settle(mine); return false; }
        return fetch(base + '/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: theirs.concat(d.codes).join(','), sections: 'cart-drawer' }) })
          .then(function (r) { return r.json(); })
          .then(function (j) { renderSections(j.sections); settle(d.codes); return true; });
      });
    });
  }
  cart.deal = function () {
    var url = window.WH_ROUTES && WH_ROUTES.deals;
    if (!url) return Promise.resolve(false);
    if (dealBusy) { dealAgain = true; return dealBusy; }
    dealBusy = dealOnce(url).catch(function () { return false; }).then(function (changed) {
      dealBusy = null;
      if (!dealAgain) return changed;
      dealAgain = false;
      return cart.deal().then(function (again) { return changed || again; });
    });
    return dealBusy;
  };
  function bagIcon() { var b = document.querySelector('[data-bag]'); return b && b.offsetParent ? b : document.querySelector('[data-bag-m]'); }
  function bagGulp() {
    var b = bagIcon(); if (!b || !WH.anim) return;
    WH.sound.play('pop');
    gsap.fromTo(b, { scaleX: 1.3, scaleY: 0.75 }, { scaleX: 1, scaleY: 1, duration: 0.5, ease: WH.sm('elastic.out(1.2,.4)', 0.5) });
  }
  /* the friend hops off the page and into the tote */
  function fly(img, done) {
    var b = bagIcon();
    if (!b || !WH.anim || !img.getBoundingClientRect) return done();
    var r = img.getBoundingClientRect(), t = b.getBoundingClientRect();
    var c = img.cloneNode(true);
    c.removeAttribute('srcset'); c.removeAttribute('sizes'); c.removeAttribute('loading');
    c.className = 'wh-flyer';
    var size = Math.min(r.width, r.height, 220);
    Object.assign(c.style, { left: (r.left + r.width / 2 - size / 2) + 'px', top: (r.top + r.height / 2 - size / 2) + 'px', width: size + 'px', height: size + 'px' });
    document.body.appendChild(c);
    var dx = t.left + t.width / 2 - (r.left + r.width / 2), dy = t.top + t.height / 2 - (r.top + r.height / 2);
    WH.sound.play('boing');
    gsap.timeline({ onComplete: function () { c.remove(); done(); } })
      .to(c, { scaleY: 0.7, scaleX: 1.2, duration: 0.12, ease: WH.sm('power1.out', 0.12), transformOrigin: '50% 100%' })
      .to(c, { keyframes: { x: [0, dx * 0.35, dx * 0.75, dx], y: [0, dy - 160, dy - 120, dy] }, scale: 0.18, rotation: 300, duration: 0.75, ease: WH.sm('power1.inOut', 0.75) });
  }
  cart.fly = fly;
  function cartUi() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-tote-open]');
      if (opener && drawer()) { e.preventDefault(); cart.open(); return; }
      if (e.target.closest('[data-tote-close]')) { if (!e.target.closest('a')) e.preventDefault(); cart.close(); return; }
      if (e.target.closest('[data-rewind]')) { e.preventDefault(); rewind(); return; }
      var q = e.target.closest('[data-tote-qty]');
      if (q) { e.preventDefault(); var li = q.closest('[data-line]'); q.disabled = true; cart.change(li.getAttribute('data-line'), q.getAttribute('data-tote-qty')); }
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && root.classList.contains('wh-is-tote')) cart.close(); });
    /* 10-30 owner: packaging, standard (free) or a gift box (snippets/wh-packaging.liquid). The box is an ordinary cart line: "gift box"
       adds one, "standard" takes it out; the cart page draws its lines on the server, so it reloads afterwards */
    document.addEventListener('change', function (e) {
      var r = e.target.closest && e.target.closest('[data-pack] input[type=radio]');
      if (!r) return;
      var box = r.closest('[data-pack]'), line = box.getAttribute('data-pack-line') || '0', onPage = !!box.closest('.wh-cartpage__form');
      box.querySelectorAll('input').forEach(function (i) { i.disabled = true; });
      var p;
      if (r.value === 'gift' && line === '0') {
        var fd = new FormData(); fd.append('id', box.getAttribute('data-pack-variant')); fd.append('quantity', '1'); fd.append('sections', 'cart-drawer');
        p = fetch((window.WH_ROUTES && WH_ROUTES.cartAdd || '/cart/add') + '.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
          .then(function (x) { return x.json(); })
          .then(function (j) { renderSections(j.sections); return cartJson(); })
          .then(function (c) { setCount(c.item_count); });
      } else if (r.value === 'standard' && line !== '0') {
        p = cart.change(line, 0);
      } else p = Promise.resolve();
      p.catch(function () {}).then(function () {
        if (onPage) { window.location.reload(); return; }
        box.querySelectorAll('input').forEach(function (i) { i.disabled = false; });
      });
    });
    /* any product form on the page (cards, scenes, product page) */
    document.addEventListener('submit', function (e) {
      var f = e.target.closest('form[data-add]');
      if (!f) return;
      e.preventDefault();
      var btn = f.querySelector('[type=submit]'), err = f.querySelector('[data-add-error]');
      if (btn) btn.disabled = true;
      if (err) err.hidden = true;
      var img = f.getAttribute('data-add-img') ? document.querySelector(f.getAttribute('data-add-img')) : null;
      var stay = f.classList.contains('wh-box__add');
      cart.add(new FormData(f), img, stay ? { stay: true, form: f } : null).catch(function (x) { if (err) { err.textContent = x.message; err.hidden = false; } })
        .then(function () { if (btn) btn.disabled = false; });
    });
    /* checkout (tote and cart page): settle the free friend first, with the cart page's typed quantities applied, then go on
       as the form would have; waits at most 7 s, after that the store's own buy-3-get-1 still applies */
    document.addEventListener('submit', function (e) {
      var s = e.submitter, f = e.target;
      if (!s || s.name !== 'checkout' || !(window.WH_ROUTES && WH_ROUTES.deals)) return;
      e.preventDefault();
      s.disabled = true; s.setAttribute('aria-busy', 'true');
      var pre = f.querySelector('[name="updates[]"]')
        ? fetch((WH_ROUTES.cart || '/cart') + '/update.js', { method: 'POST', body: new FormData(f), headers: { Accept: 'application/json' } })
        : Promise.resolve();
      pre.then(function () { return within(cart.deal(), 7000); }).catch(function () {}).then(function () {
        /* a fresh form: settling redraws the tote, which takes the original form out of the page */
        var go = document.createElement('form'), h = document.createElement('input');
        go.method = 'post'; go.action = f.getAttribute('action') || WH_ROUTES.cart || '/cart'; go.hidden = true;
        h.type = 'hidden'; h.name = 'checkout'; h.value = '1';
        go.appendChild(h); document.body.appendChild(go);
        HTMLFormElement.prototype.submit.call(go);
      });
    });
    /* a tote of 4 or more on arrival (another tab, the cart page after an update or a removal): settle it once; the cart page
       draws its lines on the server, so it reloads when the free friend changed */
    var page = document.querySelector('.wh-cartpage__form'), n = parseInt((document.querySelector('[data-cart-count]') || {}).textContent, 10) || 0;
    /* cards show what's already in the tote when the page opens */
    if (n > 0 && document.querySelector('form.wh-box__add')) cartJson().then(function (c) { markBoxes(c.items); }).catch(function () {});
    if (page || n >= 4) cart.deal().then(function (changed) {
      if (!changed || !page) return;
      var key = 'wh-deal-reload', now = Date.now();
      try { if (now - (+sessionStorage.getItem(key) || 0) < 8000) return; sessionStorage.setItem(key, String(now)); } catch (x) { return; }
      window.location.reload();
    });
  }

  /* back to the start, like rewinding the reel */
  function rewind() {
    root.classList.add('wh-is-rewind');
    WH.sound.play('rustle');
    window.scrollTo({ top: 0, behavior: WH.reduce ? 'auto' : 'smooth' });
    setTimeout(function () { root.classList.remove('wh-is-rewind'); }, 1400);
  }

  /* ---------- Fuzzy Club pop-up ---------- */
  /* CHF amounts the site states (the opening offer) restated in the visitor's currency at Shopify's rate of the day, which
     is how checkout converts a fixed-amount code; Swiss visitors keep the CHF the page was rendered with */
  function localAmounts() {
    var c = window.Shopify && window.Shopify.currency, rate = c && parseFloat(c.rate);
    if (!c || !c.active || c.active === 'CHF' || !rate) return;
    var whole = c.active === 'JPY';
    document.querySelectorAll('[data-chf]').forEach(function (s) {
      var v = parseFloat(s.getAttribute('data-chf')) * rate;
      try { s.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: c.active, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(v); } catch (e) {}
    });
  }

  function club() {
    var el = document.querySelector('[data-club]');
    if (!el) return;
    var card = el.querySelector('[data-club-card]');
    /* before the opening moment (theme setting) it announces the opening with a countdown, then it is the normal sign-up.
       The announcement is news, so it shows once even to visitors who closed the usual pop-up before; signing up ends both. */
    var soon = el.querySelector('[data-club-soon]'), usual = el.querySelector('[data-club-now]');
    var at = soon ? Date.parse(soon.getAttribute('data-at')) : NaN, timer = 0;
    /* the opening offer (theme setting; an automatic discount) is announced instead of both until it ends; it is news too, so
       it shows once more to visitors who closed an earlier pop-up. If the page was rendered before the end and is read after,
       this puts the usual one back. Signing up never reveals a code: offers go out by email. */
    var offer = el.querySelector('[data-club-offer]'), until = offer ? Date.parse(offer.getAttribute('data-until')) : NaN;
    var offerOn = function () { return !!offer && Date.now() < until; };
    if (offer) {
      var on = offerOn();
      offer.hidden = !on;
      if (on) { if (soon) soon.hidden = true; if (usual) usual.hidden = true; el.setAttribute('aria-labelledby', 'WhClubTitleOffer'); }
      else if (usual && !(soon && Date.now() < at)) { usual.hidden = false; el.setAttribute('aria-labelledby', 'WhClubTitle'); }
    }
    var key = function () { return offerOn() ? 'wh-club-offer-done' : soon && Date.now() < at ? 'wh-club-soon-done' : 'wh-club-done'; };
    var done = false;
    try { done = localStorage.getItem(key()) === '1' || localStorage.getItem('wh-club-joined') === '1'; } catch (e) {}
    var two = function (n) { return (n < 10 ? '0' : '') + n; };
    function countdown() {
      var left = at - Date.now(), on = left > 0;
      soon.hidden = !on;
      if (usual) usual.hidden = on;
      el.setAttribute('aria-labelledby', on ? 'WhClubTitleSoon' : 'WhClubTitle');
      if (!on) { clearInterval(timer); timer = 0; return; }
      var s = Math.floor(left / 1000), v = { d: String(Math.floor(s / 86400)), h: two(Math.floor(s / 3600) % 24), m: two(Math.floor(s / 60) % 60), s: two(s % 60) };
      soon.querySelectorAll('[data-u]').forEach(function (b) { var t = v[b.getAttribute('data-u')]; if (b.textContent !== t) b.textContent = t; });
    }
    if (soon && !isNaN(at) && !offerOn()) countdown();
    function open() {
      if (done || !el.hidden) return;
      if (soon && !isNaN(at) && !offerOn()) { countdown(); if (!timer) timer = setInterval(countdown, 1000); }
      el.hidden = false;
      requestAnimationFrame(function () { el.classList.add('is-open'); });
      if (WH.anim) gsap.fromTo(card, { y: 160, rotation: -6 }, { y: 0, rotation: 0, duration: 0.6, ease: WH.sm('back.out(1.8)', 0.6) });
      WH.sound.play('squeak');
    }
    function close() {
      done = true;
      clearInterval(timer); timer = 0;
      try { localStorage.setItem(key(), '1'); } catch (e) {}
      el.classList.remove('is-open');
      setTimeout(function () { el.hidden = true; }, 380);
    }
    el.querySelectorAll('[data-club-close]').forEach(function (b) { b.addEventListener('click', close); });
    el.addEventListener('click', function (e) { if (e.target === el) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !el.hidden) close(); });
    var delay = (parseFloat(el.getAttribute('data-delay')) || 14) * 1000, armed = false;
    setTimeout(function () { armed = true; }, Math.min(delay, 6000));
    setTimeout(open, delay);
    /* only measures the page while the pop-up could still open (seen or closed: nothing to do on scroll) */
    window.addEventListener('scroll', function () { if (done || !armed || !el.hidden) return; if (window.scrollY > document.documentElement.scrollHeight * 0.35) open(); }, { passive: true });
    var form = el.querySelector('form'), err = el.querySelector('[data-club-err]');
    function joined() {
      el.classList.add('is-joined');
      var code = el.querySelector('[data-club-code]'); if (code) code.hidden = false;
      if (form) form.hidden = true;
      if (err) err.hidden = true;
      try { ['wh-club-done', 'wh-club-soon-done', 'wh-club-offer-done', 'wh-club-joined'].forEach(function (k) { localStorage.setItem(k, '1'); }); } catch (x) {}
      if (WH.anim) WH.puff(window.innerWidth / 2, window.innerHeight * 0.55, 18, true);
      WH.sound.play('ding');
    }
    /* only say "you're in" when Shopify says so: it answers a real sign-up by redirecting to ?customer_posted=true.
       If it wants a human check (/challenge), send the visitor through the normal form post so they can do it; Shopify brings
       them back here with customer_posted=true and the code is shown then. Anything else (bad email, network) is an error. */
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[type=submit]'); if (btn) btn.disabled = true;
      if (err) err.hidden = true;
      var fail = function () { if (btn) btn.disabled = false; if (err) err.hidden = false; };
      fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (/[?&]customer_posted=true/.test(r.url)) return joined();
          if (/\/challenge/.test(r.url)) { try { sessionStorage.setItem('wh-club-sent', '1'); } catch (x) {} return form.submit(); }
          fail();
        }, fail);
    });
    var back = false;
    try { back = sessionStorage.getItem('wh-club-sent') === '1'; sessionStorage.removeItem('wh-club-sent'); } catch (x) {}
    if (back && /[?&]customer_posted=true/.test(location.search)) { done = false; open(); joined(); }
  }

  /* ---------- scene gloves ---------- */
  /* Over a scene marked [data-glove] (the orchard) a felt glove rides on the pointer instead of the plain cursor. Its
     fingertip is the click point; it swings against the motion like something hanging from your finger, reaches a little
     over anything clickable, closes into a fist while the button is down and gives a small tug when you let go on a fruit.
     Mouse only. The plain glove cursor in wh-scenes.css stays until the pictures have loaded. Per frame it writes only
     transform (the radar's recorder skips that). */
  function gloves() {
    if (!WH.fine) return;
    document.querySelectorAll('[data-glove]').forEach(function (stage) {
      var hot = (stage.getAttribute('data-glove-hot') || '0.5 0').split(' ').map(parseFloat);
      var el = document.createElement('div'), open = new Image(), grab = new Image(), ready = false;
      el.className = 'wh-glove';
      el.setAttribute('aria-hidden', 'true');
      el.style.transformOrigin = (hot[0] * 100).toFixed(2) + '% ' + (hot[1] * 100).toFixed(2) + '%';
      open.className = 'wh-glove__open'; grab.className = 'wh-glove__grab'; open.alt = grab.alt = '';
      open.onload = function () { ready = true; };
      open.src = stage.getAttribute('data-glove-open'); grab.src = stage.getAttribute('data-glove-grab');
      el.appendChild(open); el.appendChild(grab);
      document.body.appendChild(el);
      var w = 0, h = 0, x = 0, y = 0, lastX = 0, vx = 0, ang = 0, av = 0, sc = 1, sv = 0, dy = 0, dyv = 0;
      var on = false, down = false, reach = false, still = true;
      function place() {
        el.style.transform = 'translate3d(' + (x - w * hot[0]).toFixed(1) + 'px,' + (y - h * hot[1] + dy).toFixed(1) + 'px,0) rotate(' + ang.toFixed(2) + 'deg) scale(' + sc.toFixed(3) + ')';
      }
      function show(e) {
        if (!ready || on) return;
        on = true; still = false;
        w = el.offsetWidth; h = el.offsetHeight; x = lastX = e.clientX; y = e.clientY; vx = 0; ang = av = dy = dyv = sv = 0; sc = 1;
        place();
        stage.classList.add('is-gloved'); el.classList.add('is-on');
      }
      function hide() {
        if (!on) return;
        on = false; down = false;
        stage.classList.remove('is-gloved'); el.classList.remove('is-on', 'is-grab');
      }
      stage.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') show(e); });
      stage.addEventListener('pointerleave', hide);
      window.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        if (!on) { if (stage.contains(e.target)) show(e); if (!on) return; }
        vx = vx * 0.5 + (e.clientX - lastX) * 0.5; lastX = e.clientX;
        x = e.clientX; y = e.clientY;
        reach = !!(e.target.closest && e.target.closest('a, button, [role="button"], label'));
        still = false;
      }, { passive: true });
      stage.addEventListener('pointerdown', function (e) { if (on && e.button === 0) { down = true; el.classList.add('is-grab'); still = false; } });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false; el.classList.remove('is-grab');
        if (reach) dyv += 2.2;
        still = false;
      });
      window.addEventListener('blur', hide);
      document.addEventListener('visibilitychange', function () { if (document.hidden) hide(); });
      /* scrolling moves the page under a still pointer: let go of the glove once the scene has scrolled away from it */
      window.addEventListener('scroll', function () {
        if (!on) return;
        var u = document.elementFromPoint(x, y);
        if (!u || !stage.contains(u)) hide();
      }, { passive: true });
      WH.onTick(function (dt) {
        if (!on || still) return;
        var f = Math.min(3, dt * 60);
        vx *= Math.pow(0.82, f);
        var ta = WH.clamp(vx * 1.6, -26, 26) + (reach && !down ? -5 : 0), ts = down ? 0.9 : reach ? 1.08 : 1;
        if (WH.reduce) { ang = av = dy = dyv = sv = 0; sc = ts; }
        else {
          av += ((ta - ang) * 0.1 - av * 0.17) * f; ang += av * f;
          sv += ((ts - sc) * 0.22 - sv * 0.26) * f; sc += sv * f;
          dyv += (-dy * 0.2 - dyv * 0.24) * f; dy += dyv * f;
        }
        place();
        still = (WH.reduce || (Math.abs(ta - ang) < 0.05 && Math.abs(av) < 0.01 && Math.abs(ts - sc) < 0.002 && Math.abs(sv) < 0.001 &&
          Math.abs(dy) < 0.05 && Math.abs(dyv) < 0.02)) && Math.abs(vx) < 0.05;
      });
    });
  }

  /* ---------- end credits ---------- */
  function credits() {
    var c = document.querySelector('[data-credits]');
    if (!c) return;
    var list = c.querySelector('[data-credits-roll]');
    if (!list || WH.reduce) return;
    var y = 0, paused = false, h = 0;
    var clone = list.cloneNode(true); clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('a').forEach(function (a) { a.tabIndex = -1; });
    list.parentNode.appendChild(clone);
    var measure = function () { h = list.offsetHeight; };
    measure(); window.addEventListener('resize', measure);
    c.addEventListener('pointerenter', function () { paused = true; });
    c.addEventListener('pointerleave', function () { paused = false; });
    c.addEventListener('focusin', function () { paused = true; });
    c.addEventListener('focusout', function () { paused = false; });
    var visible = false;
    new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }).observe(c);
    WH.onTick(function (dt) {
      if (!visible || paused || !h) return;
      y = (y + 36 * dt) % h;
      list.style.transform = clone.style.transform = 'translateY(' + (-y).toFixed(1) + 'px)';
    });
  }

  /* ---------- we don't deliver to your country yet ---------- */
  /* Shopify's geolocation says where the visitor is (once per session); not one of the active markets -> the note above the
     ribbon, with the country named in the page's language. Closing it is remembered for that country. ?wh_geo=GB tries it. */
  function noShip() {
    var bar = document.querySelector('[data-noship]');
    if (!bar) return;
    var served = (bar.getAttribute('data-served') || '').split(',');
    function show(cc) {
      if (!cc || served.indexOf(cc) > -1) return;
      try { if (localStorage.getItem('wh-noship-closed') === cc) return; } catch (e) {}
      var name = cc;
      try { name = new Intl.DisplayNames([root.lang || 'en'], { type: 'region' }).of(cc) || cc; } catch (e) {}
      bar.querySelector('[data-noship-text]').textContent = bar.getAttribute('data-text').replace('%C%', name);
      bar.hidden = false;
      bar.querySelector('[data-noship-close]').addEventListener('click', function () {
        bar.hidden = true;
        try { localStorage.setItem('wh-noship-closed', cc); } catch (e) {}
      });
    }
    var test = (location.search.match(/[?&]wh_geo=([A-Z]{2})\b/) || [])[1];
    if (test) return show(test);
    var known = null;
    try { known = sessionStorage.getItem('wh-geo'); } catch (e) {}
    if (known) return show(known);
    fetch('/browsing_context_suggestions.json?country%5Benabled%5D=true', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var cc = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
        if (!cc) return;
        try { sessionStorage.setItem('wh-geo', cc); } catch (e) {}
        show(cc);
      })
      .catch(function () {});
  }

  /* ---------- boot ---------- */
  /* nothing on the page is dragged out as a ghost picture (scene art, friends, links); text fields still work */
  function noDrag() {
    document.addEventListener('dragstart', function (e) {
      var t = e.target;
      if (t && t.closest && !t.closest('input, textarea, [contenteditable="true"]')) e.preventDefault();
    });
  }

  function boot() {
    if (WH.reduce) root.classList.add('wh-is-still');
    header(); localAmounts(); fluffClicks(); clapper(); cartUi(); club(); credits(); noDrag(); gloves(); noShip();
    WH.boil(); WH.pops();
    root.classList.add('wh-is-ready');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
